import type { StrainSummary } from '@shared';
import { doseProductDisplayName, isUsableThcPercent, parseDoseWeight, thcMgContribution } from './doseCalculator';
import type { JournalEntry, RitualAllocation, RitualOrigin, RitualTargetMode } from './journalStore';

export const RITUAL_DISPLAY_DECIMALS = 2;

export function formatRitualNumber(value: number): string {
  return Number.isFinite(value) ? value.toFixed(RITUAL_DISPLAY_DECIMALS) : (0).toFixed(RITUAL_DISPLAY_DECIMALS);
}

export function formatRitualInput(value: string): string {
  return value.trim() ? formatRitualNumber(parseDoseWeight(value)) : '';
}

export interface RitualAllocationDraft {
  product: StrainSummary | null;
  snapshot: RitualAllocation | null;
  weightInput: string;
  doseInput: string;
}
export interface RitualDraft {
  title: string;
  occurredAt: string;
  allocations: RitualAllocationDraft[];
  allowAllocationlessLegacySave: boolean;
  targetMode: RitualTargetMode;
  mood: string;
  effects: string;
  notes: string;
  blendLabel: string;
  origin: RitualOrigin;
}
export interface RitualSaveValue {
  title: string;
  occurredAt: string;
  allocations: RitualAllocation[];
  targetMode: RitualTargetMode;
  mood: string;
  effects: string;
  notes: string;
  blendLabel: string;
  origin: RitualOrigin;
}

export function emptyRitualAllocation(): RitualAllocationDraft {
  return { product: null, snapshot: null, weightInput: '', doseInput: '' };
}
export function localDateTimeValue(date = new Date()): string {
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return shifted.toISOString().slice(0, 16);
}
export function weightFromThcDose(thcDoseMg: string | number, thcPercent: number | null | undefined): number {
  const dose = parseDoseWeight(thcDoseMg);
  return isUsableThcPercent(thcPercent ?? 0) ? dose / ((thcPercent as number) * 10) : 0;
}
export function allocationPotency(row: RitualAllocationDraft): number | null {
  return row.product?.thcPercent ?? row.snapshot?.thcPercent ?? null;
}
export function allocationCbdPotency(row: RitualAllocationDraft): number | null {
  return row.product?.cbdPercent ?? row.snapshot?.cbdPercent ?? null;
}
export function calculateRitualAllocation(row: RitualAllocationDraft, mode: RitualTargetMode, single: boolean): { weightGrams: number; thcDoseMg: number; cbdDoseMg: number } {
  const potency = allocationPotency(row);
  if (single && mode === 'dose') {
    const thcDoseMg = parseDoseWeight(row.doseInput);
    const weightGrams = weightFromThcDose(thcDoseMg, potency);
    return { weightGrams, thcDoseMg, cbdDoseMg: thcMgContribution(weightGrams, allocationCbdPotency(row) ?? 0) };
  }
  const weightGrams = parseDoseWeight(row.weightInput);
  return {
    weightGrams,
    thcDoseMg: thcMgContribution(weightGrams, potency ?? 0),
    cbdDoseMg: thcMgContribution(weightGrams, allocationCbdPotency(row) ?? 0),
  };
}
export function ritualTotals(rows: RitualAllocationDraft[], mode: RitualTargetMode): { totalWeightGrams: number; totalThcDoseMg: number } {
  return rows.reduce((totals, row) => {
    const result = calculateRitualAllocation(row, mode, rows.length === 1);
    return { totalWeightGrams: totals.totalWeightGrams + result.weightGrams, totalThcDoseMg: totals.totalThcDoseMg + result.thcDoseMg };
  }, { totalWeightGrams: 0, totalThcDoseMg: 0 });
}
export function snapshotAllocation(row: RitualAllocationDraft, mode: RitualTargetMode, single: boolean): RitualAllocation {
  const product = row.product;
  return {
    productId: product?.id ?? row.snapshot?.productId ?? null,
    brand: product?.brand?.trim() ?? row.snapshot?.brand ?? '',
    productName: product
      ? product.productName?.trim() || product.name?.trim() || 'Unnamed product'
      : row.snapshot?.productName ?? '',
    thcPercent: allocationPotency(row),
    cbdPercent: allocationCbdPotency(row),
    ...calculateRitualAllocation(row, mode, single),
  };
}

export function switchRitualTargetMode(draft: RitualDraft, targetMode: RitualTargetMode): RitualDraft {
  if (draft.allocations.length !== 1 || draft.targetMode === targetMode) return draft;
  const row = draft.allocations[0]!;
  const current = calculateRitualAllocation(row, draft.targetMode, true);
  return {
    ...draft,
    targetMode,
    allocations: [{ ...row, weightInput: formatRitualNumber(current.weightGrams), doseInput: formatRitualNumber(current.thcDoseMg) }],
  };
}

export function addRitualAllocation(draft: RitualDraft): RitualDraft {
  if (draft.allocations.length === 0) {
    return { ...draft, allocations: [emptyRitualAllocation()] };
  }
  const first = draft.allocations[0]!;
  const preservedFirst = draft.targetMode === 'dose'
    ? { ...first, weightInput: formatRitualNumber(calculateRitualAllocation(first, 'dose', true).weightGrams) }
    : first;
  return { ...draft, targetMode: 'weight', allocations: [preservedFirst, ...draft.allocations.slice(1), emptyRitualAllocation()] };
}

export function removeRitualAllocation(draft: RitualDraft, index: number): RitualDraft {
  return draft.allocations.length <= 1
    ? draft
    : { ...draft, allocations: draft.allocations.filter((_, rowIndex) => rowIndex !== index) };
}

export function replaceRitualProduct(draft: RitualDraft, index: number, product: StrainSummary): RitualDraft {
  return {
    ...draft,
    allocations: draft.allocations.map((row, rowIndex) => rowIndex === index
      ? { ...row, product, snapshot: null }
      : row),
  };
}

export function occurredAtIso(value: string): string | null {
  if (!value.trim()) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

export function buildRitualSaveValue(draft: RitualDraft): { value: RitualSaveValue | null; error: string | null } {
  const occurredAt = occurredAtIso(draft.occurredAt);
  if (!occurredAt) return { value: null, error: 'Enter a valid Date & Time.' };
  const targetMode: RitualTargetMode = draft.allocations.length > 1 ? 'weight' : draft.targetMode;
  const calculated = draft.allocations.map((row) => calculateRitualAllocation(row, targetMode, draft.allocations.length === 1));
  const validConsumption = draft.allocations.length === 0
    ? draft.allowAllocationlessLegacySave
    : draft.allocations.every((row, index) =>
      Boolean(row.product || row.snapshot?.productName) && calculated[index]!.weightGrams > 0,
    );
  if (!draft.title.trim() || !validConsumption) return { value: null, error: null };
  return {
    value: {
      title: draft.title.trim(),
      occurredAt,
      allocations: draft.allocations.map((row) => snapshotAllocation(row, targetMode, draft.allocations.length === 1)),
      targetMode,
      mood: draft.mood.trim(),
      effects: draft.effects.trim(),
      notes: draft.notes.trim(),
      blendLabel: draft.blendLabel,
      origin: draft.origin,
    },
    error: null,
  };
}
export function draftFromEntry(entry: JournalEntry): RitualDraft {
  return {
    title: entry.title,
    occurredAt: localDateTimeValue(new Date(entry.occurredAt)),
    allocations: entry.allocations.map((snapshot) => ({ product: null, snapshot, weightInput: formatRitualNumber(snapshot.weightGrams), doseInput: formatRitualNumber(snapshot.thcDoseMg) })),
    allowAllocationlessLegacySave: entry.allocations.length === 0,
    targetMode: entry.allocations.length > 1 ? 'weight' : entry.targetMode,
    mood: entry.mood,
    effects: entry.effects,
    notes: entry.notes,
    blendLabel: entry.blendLabel,
    origin: entry.origin,
  };
}
export function defaultRitualTitle(rows: RitualAllocationDraft[]): string {
  const names = rows.map((row) => row.product ? doseProductDisplayName(row.product) : row.snapshot?.productName ?? '').filter(Boolean);
  if (names.length === 1) return names[0]!;
  if (names.length > 1) return `${names[0]} + ${names.length - 1} more`;
  return '';
}

export function ritualDraftFromProducts(
  rows: Array<{ product: StrainSummary; weightGrams: number; thcDoseMg?: number }>,
  targetMode: RitualTargetMode = 'weight',
  origin: RitualOrigin = 'manual',
): RitualDraft {
  const allocations = rows.map(({ product, weightGrams, thcDoseMg }) => ({
    product,
    snapshot: null,
    weightInput: formatRitualNumber(weightGrams),
    doseInput: formatRitualNumber(thcDoseMg ?? thcMgContribution(weightGrams, product.thcPercent)),
  }));
  return {
    title: defaultRitualTitle(allocations),
    occurredAt: localDateTimeValue(),
    allocations: allocations.length > 0 ? allocations : [emptyRitualAllocation()],
    allowAllocationlessLegacySave: false,
    targetMode: allocations.length > 1 ? 'weight' : targetMode,
    mood: '', effects: '', notes: '', blendLabel: '',
    origin,
  };
}
